import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import NewLayout from '../layout/newLayout';
import SEO from '../components/seo';
import Hero from '../components/tips/Hero';
import InfluencersSection from '../components/tips/InfluencersSection';
import UsesSection from '../components/tips/UsesSection';
import ProductsSection from '../components/tips/ProductsSection';
import RelatedArticlesSection from '../components/tips/RelatedArticlesSection';
import SocialSection from '../components/tips/SocialSection';
import { tipsPageData } from '../data/tipsPageData';
import { sortInfluencersBySpecificOrder } from '../utils/influencerSorter';

export default function TipsPage({ pageContext }) {
  const { page } = pageContext;
  
  const data = useStaticQuery(graphql`
    query {
      dato {
        allArticles(orderBy: dateOfArticle_DESC) {
          id
          title
          slug
          excerpt
          dateOfArticle
          category {
            id
            name
            slug
          }
          tags
          heroImage {
            url
            alt
          }
        }
        allTipsInfluencers {
          id
          name
          category
          hero {
            url
            alt
          }
          avatar {
            url
            alt
          }
          bio
          instagram
          xUsername
          youtube
          facebook
          tiktok
          externalVideo {
            url
            provider
            providerUid
            thumbnailUrl
          }
          favoriteProducts {
            id
            name
            listImage {
              url
              alt
            }
            whereToBuy
          }
        }
      }
    }
  `);
  
  const getRandomTags = (tagsArray) => {
    if (!tagsArray || tagsArray.length === 0) {
      return [];
    }
    
    if (tagsArray.length <= 2) {
      return tagsArray.map(tag => tag.toUpperCase());
    }
    
    const tags = [...tagsArray];
    const result = [];
    
    for (let i = 0; i < 2; i++) {
      const randomIndex = Math.floor(Math.random() * tags.length);
      result.push(tags[randomIndex].toUpperCase());
      tags.splice(randomIndex, 1);
    }
    
    return result;
  };
  
  const realArticles = data.dato.allArticles.map(article => {
    const categories = article.tags && article.tags.length > 0
      ? getRandomTags(article.tags)
      : [article.category.name.toUpperCase()];
    
    return {
      id: article.id,
      title: article.title,
      categories: categories,
      image: (article.heroImage && article.heroImage.url) || "/img/tips-page/gallery01.png",
      url: `/article/${article.slug}/`
    };
  });
  
  const relatedArticles = {
    title: "SEVEN DECADES OF KNOW-HOW",
    articles: realArticles
  };
  
  const influencerData = data.dato.allTipsInfluencers;
  
  const mapCategoryName = (category) => {
    const categoryMap = {
      'automotive': 'AUTOMOTIVE',
      'construction': 'CONSTRUCTION/DIY',
      'machine repair': 'MACHINE REPAIR & OPERATIONS'
    };
    
    const lowerCategory = category.toLowerCase();
    
    return categoryMap[lowerCategory] || category.toUpperCase();
  };
  
  const uniqueCategories = influencerData && influencerData.length > 0 
    ? [...new Set(influencerData.map(inf => mapCategoryName(inf.category)))]
    : [];
  
  const mappedInfluencers = influencerData ? influencerData.map(influencer => {
    const favoriteProducts = influencer.favoriteProducts ? influencer.favoriteProducts.map(product => ({
      id: product.id,
      name: product.name,
      image: (product.listImage && product.listImage.url) || "/img/tips-page/gallery01.png",
      whereToBuy: product.whereToBuy || ""
    })) : [];
    
    return {
      id: influencer.id,
      title: influencer.name,
      category: mapCategoryName(influencer.category),
      url: (influencer.avatar && influencer.avatar.url) || "/img/tips-page/gallery01.png",
      alt: influencer.avatar && influencer.avatar.alt,
      details: {
        headerImage: (influencer.hero && influencer.hero.url) || "/img/tips-page/gallery01.png",
        profileImage: (influencer.avatar && influencer.avatar.url) || "/img/tips-page/gallery01.png",
        introText: influencer.bio || "",
        socialMedia: [
          influencer.instagram && { platform: 'instagram', url: `https://www.instagram.com/${influencer.instagram}` },
          influencer.xUsername && { platform: 'twitter', url: `https://twitter.com/${influencer.xUsername}` },
          influencer.youtube && { platform: 'youtube', url: `https://www.youtube.com/${influencer.youtube}` },
          influencer.facebook && { platform: 'facebook', url: `https://www.facebook.com/${influencer.facebook}` },
          influencer.tiktok && { platform: 'tiktok', url: `https://www.tiktok.com/@${influencer.tiktok}` }
        ].filter(Boolean),
        video: influencer.externalVideo ? (function() {
          var embedUrl;
          if (influencer.externalVideo.provider === 'vimeo') {
            var parts = influencer.externalVideo.url.split('/');
            var videoId = parts[parts.length - 2];
            var hash = parts[parts.length - 1];
            
            embedUrl = 'https://player.vimeo.com/video/' + videoId + '?h=' + hash;
          } else {
            embedUrl = influencer.externalVideo.url;
          }
          console.log('Generated embed URL for ' + influencer.name + ':', embedUrl);
          
          return {
            videoUrl: influencer.externalVideo.url,
            title: influencer.name + ' Video',
            provider: influencer.externalVideo.provider,
            embedUrl: embedUrl
          };
        })() : null,
        favoriteProducts: favoriteProducts
      }
    }
  }) : [];
  
  const sortedInfluencers = sortInfluencersBySpecificOrder(mappedInfluencers);
  
  const influencers = {
    title: "wd-40 BRAND® pros",
    pills: uniqueCategories,
    gallery: sortedInfluencers
  };
  
  return (
    <NewLayout>
      {page && <SEO page={page} />}

      {/* HERO SECTION */}
      {page && (
        <Hero
          title={page.title || (page.seo && page.seo.title)}
          image={
            (page.heroDesktop && page.heroDesktop.url) ||
            (page.heroImage && page.heroImage.url)
          }
          mobileimage={
            (page.heroMobile && page.heroMobile.url) ||
            (page.heroImage && page.heroImage.url)
          }
        />
      )}
      
      {/* INFLUENCERS SECTION */}
      <InfluencersSection data={influencers} />

      {/* PRODUCTS SECTION */}
      <ProductsSection data={tipsPageData.productSection} />

      {/* USES SECTION */}
      <UsesSection data={tipsPageData.usesSection} />

      {/* RELATED ARTICLES SECTION */}
      <RelatedArticlesSection data={relatedArticles} />

      {/* SOCIAL SECTION */}
      <SocialSection />
    </NewLayout>
  );
}
